// Утилита определения городов и их принадлежности
// Город = центральный квартал + смежные базовые кварталы

import {HexMath} from './HexMath';
import {checkIfCentralQuarter} from "./buildings/checkIfCentralQuarter";
import {CENTRAL_QUARTER_PREFIX, CITY_COMMON_BUILDING_TOKENS, KEY_POINT_TOKENS} from "../constants/tokens";
import {checkIfKeyPoint} from "./buildings/checkIfKeyPoint";

/**
 * Получить цвет игрока из токена центрального квартала
 * Возвращает 'neutral' для нейтральных городов
 */
export const getPlayerColorFromCentralQuarter = (token) => {
  // Центральный квартал игрока (central_quarter_color)
  if (checkIfCentralQuarter(token)) {
    return token.replace(CENTRAL_QUARTER_PREFIX, '');
  }
  return null;
};

/**
 * Класс для определения городов на поле
 */
export class CityDetector {
  constructor(hexMath) {
    this.hexMath = hexMath || new HexMath(30, 'flat');
  }

  /**
   * Преобразовать ключ ячейки (q,r) в координаты {q, r}
   * @param {string} key - Ключ ячейки в формате "q,r"
   * @returns {Object} Координаты {q, r}
   */
  keyToCoord(key) {
    const [q, r] = key.split(',').map(Number);
    return {q, r};
  }

  /**
   * Преобразовать координаты в ключ ячейки
   * @param {Object} coords - Координаты {q, r}
   * @returns {string} Ключ ячейки в формате "q,r"
   */
  coordToKey(coords) {
    return `${coords.q},${coords.r}`;
  }

  /**
   * Получить соседние координаты
   * @param {Object} coords - Координаты {q, r}
   * @returns {Array} Массив соседних координат
   */
  getNeighborCoords(coords) {
    const {q, r} = coords;
    // Для flat-top ориентации
    return [
      {q: q + 1, r: r},
      {q: q - 1, r: r},
      {q: q, r: r + 1},
      {q: q, r: r - 1},
      {q: q + 1, r: r - 1},
      {q: q - 1, r: r + 1}
    ];
  }

  /**
   * Получить расстояние между двумя координатами
   * @param {Object} coords1 - Первые координаты {q, r}
   * @param {Object} coords2 - Вторые координаты {q, r}
   * @returns {number} Расстояние
   */
  getDistance(coords1, coords2) {
    const q1 = coords1.q, r1 = coords1.r;
    const q2 = coords2.q, r2 = coords2.r;
    const s1 = -q1 - r1;
    const s2 = -q2 - r2;
    return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
  }

  /**
   * Получить все центральные кварталы игроков (включая нейтральные)
   */
  findCentralQuarters(hexes) {
    const centralQuarters = [];

    for (const [key, token] of Object.entries(hexes)) {
      if (checkIfCentralQuarter(token)) {
        const coords = this.keyToCoord(key);
        const playerColor = getPlayerColorFromCentralQuarter(token);
        centralQuarters.push({
          hexKey: key,
          coords,
          token,
          playerColor
        });
      }
    }

    return centralQuarters;
  }

  /**
   * Найти все базовые кварталы
   */
  findBasicQuarters(hexes) {
    const basicQuarters = [];

    for (const [key, token] of Object.entries(hexes)) {
      if (token === 'quarter') {
        const coords = this.keyToCoord(key);
        basicQuarters.push({
          hexKey: key,
          coords,
          token
        });
      }
    }

    return basicQuarters;
  }

  /**
   * Проверить, является ли квартал частью города (смежный к центральному)
   */
  isQuarterPartOfCity(quarterHexKey, centralQuarterHexKey) {
    const quarterCoords = this.keyToCoord(quarterHexKey);
    const centralCoords = this.keyToCoord(centralQuarterHexKey);

    // Проверяем смежность (расстояние = 1)
    const distance = this.getDistance(quarterCoords, centralCoords);
    return distance === 1;
  }

  /**
   * Определить все города на поле, но без цчета специальных строений.
   * !!! Не использовать напрямую - для получения полного состояния городов использовать calculateCitiesCache
   * Возвращает объект, организованный по цветам игроков с массивом городов для каждого:
   * {
   *   'blue': [
   *     {
   *       centralQuarter: '1,2',        // коородината центрального квартала
   *       quarter: ['1,3', '2,2'],  // координаты базовых кварталов
   *       farm: ['1,4'],                // координаты ферм
   *       barracks: ['2,3'],             // координаты казарм
   *       mine: ['0,2'],                // координаты рудников
   *       super_mine: [],                // координаты великих рудников
   *       ruins: ['3,1'],                 // координаты руин
   *       super_ruins: []                 // координаты великих руин
   *     }
   *   ],
   *   'neutral': [...]
   * }
   */
  detectCityHexes(hexes) {
    // Результат: { [playerColor]: [city1, city2, ...] }
    const citiesByColor = {};
    const centralQuarters = this.findCentralQuarters(hexes);
    const basicQuarters = this.findBasicQuarters(hexes);

    // Для каждого центрального квартала создаем город
    for (const cq of centralQuarters) {
      const playerColor = cq.playerColor;

      // Инициализируем массив городов для этого цвета, если нужно
      if (!citiesByColor[playerColor]) {
        citiesByColor[playerColor] = [];
      }

      // Находим базовые кварталы, принадлежащие этому городу
      const cityQuarters = basicQuarters.filter(q =>
        this.isQuarterPartOfCity(q.hexKey, cq.hexKey, hexes)
      );

      // Находим все ключевые точки, принадлежащих городу
      const cityKeyPoints = this.findCityKeyPoints(
        [cq.hexKey, ...cityQuarters.map(q => q.hexKey)],
        hexes
      );

      // Находим строения, принадлежащие городу
      const cityBuildings = this.findCityBuildings(
        [cq.hexKey, ...cityQuarters.map(q => q.hexKey)],
        hexes
      );

      // Создаем объект состояния города
      const cityState = {
        centralQuarter: cq.hexKey,
        [CITY_COMMON_BUILDING_TOKENS.QUARTER]: cityQuarters.map(q => q.hexKey),
        [CITY_COMMON_BUILDING_TOKENS.FARM]: cityBuildings.filter(hexKey => hexes[hexKey] === CITY_COMMON_BUILDING_TOKENS.FARM),
        [CITY_COMMON_BUILDING_TOKENS.BARRACKS]: cityBuildings.filter(hexKey => hexes[hexKey] === CITY_COMMON_BUILDING_TOKENS.BARRACKS),
      };

      Object.keys(KEY_POINT_TOKENS).forEach(key => {
        const value = KEY_POINT_TOKENS[key];
        cityState[value] = cityKeyPoints.filter(hexKey => hexes[hexKey] === value);
      });

      // Добавляем город в массив для соответствующего цвета
      citiesByColor[playerColor].push(cityState);
    }

    return citiesByColor;
  }

  /**
   * Найти ключевые точки, принадлежащие городу
   * (смежные к кварталам города)
   */
  findCityKeyPoints(cityQuarterKeys, hexes) {
    const keyPoints = [];

    // Для каждого квартала города проверяем соседние ячейки
    for (const quarterKey of cityQuarterKeys) {
      const quarterCoords = this.keyToCoord(quarterKey);
      const neighbors = this.getNeighborCoords(quarterCoords);

      for (const neighbor of neighbors) {
        const neighborKey = this.coordToKey(neighbor);
        const token = hexes[neighborKey];

        if (token && checkIfKeyPoint(token) && !keyPoints.includes(neighborKey)) {
          keyPoints.push(neighborKey);
        }
      }
    }

    return keyPoints;
  }

  /**
   * Найти строения, принадлежащие городу
   * (фермы и казармы, смежные к кварталам города)
   */
  findCityBuildings(cityQuarterKeys, hexes) {
    const buildings = [];

    for (const quarterKey of cityQuarterKeys) {
      const quarterCoords = this.keyToCoord(quarterKey);
      const neighbors = this.getNeighborCoords(quarterCoords);

      for (const neighbor of neighbors) {
        const neighborKey = this.coordToKey(neighbor);
        const token = hexes[neighborKey];

        if ((token === CITY_COMMON_BUILDING_TOKENS.FARM || token === CITY_COMMON_BUILDING_TOKENS.BARRACKS)
          && !buildings.includes(neighborKey)
        ) {
          buildings.push(neighborKey);
        }
      }
    }

    return buildings;
  }
}

export default CityDetector;
